import type { TeamWith } from "@redotech/account-common/base-team";
import { PurchaseLabelType } from "@redotech/merchant-sdk/rpc/schema/purchase-label";
import { MerchantAppReturn } from "@redotech/redo-return-core-common/return";
import { MAX_LABEL_PRICE_DEFAULT } from "@redotech/redo-return-core-common/return-shipment-types";
import { getIsFirstLabel } from "@redotech/redo-return-core-common/return-shipment-utils";
import type { ReturnsSettings } from "@redotech/redo-return-settings-common/returns-settings";
import { useMemo } from "react";
import { useDropoffOptions } from "../../../hooks/use-dropoff-options";
import { useLabelRates } from "../../../hooks/use-label-rates";
import type { CreateLabelForm } from "../create-label-modal-forms";
import { getReturnShippingMerchantCost } from "../create-label-modal-utils";

export function useReturnShippingRatesAndCost({
  team,
  return_,
  createLabelInput,
  ratesEnabled,
  shipmentId,
  labelType,
  editingDropoffGroupId,
}: {
  team: TeamWith<{ returns?: ReturnsSettings }> | undefined;
  return_: MerchantAppReturn;
  createLabelInput: CreateLabelForm;
  ratesEnabled: boolean;
  shipmentId: string | undefined;
  labelType: PurchaseLabelType;
  editingDropoffGroupId: string | undefined;
}) {
  const shopperSelectedAddress =
    createLabelInput.inputs.shopperSelectedAddress.value;
  const fromAddress =
    shopperSelectedAddress === "custom"
      ? createLabelInput.inputs.customShipFromAddress.value
      : shopperSelectedAddress;

  const merchantSelectedAddress =
    createLabelInput.inputs.merchantSelectedAddress.value;
  const toAddress =
    merchantSelectedAddress === "custom"
      ? createLabelInput.inputs.customShipToAddress.value
      : merchantSelectedAddress;

  // Only fetch rates on the create label page — disable on confirmation/pending
  // to prevent refetches triggered by query invalidation after label purchase.
  const labelRatesQuery = useLabelRates({
    teamId: team?._id ?? "",
    returnId: return_.id,
    fromAddress: ratesEnabled ? fromAddress : undefined,
    toAddress: ratesEnabled ? toAddress : undefined,
    weight: {
      value: createLabelInput.inputs.labelWeight.value.weight,
      unit: createLabelInput.inputs.labelWeight.value.unit,
    },
    originalShipmentId: shipmentId,
    labelType,
  });

  // Resolved without the slow carrier-rate fetch; re-keys on the chosen ship-to
  // since drop-off eligibility is a property of that address.
  const dropoffOptionsQuery = useDropoffOptions({
    teamId: team?._id ?? "",
    returnId: return_.id,
    fromCountry: fromAddress?.country,
    merchantAddress: ratesEnabled ? toAddress : undefined,
  });

  const rates = useMemo(
    () => labelRatesQuery.data?.rates ?? [],
    [labelRatesQuery.data?.rates],
  );

  const dropoffOptions = useMemo(
    () => dropoffOptionsQuery.data?.dropoffOptions ?? [],
    [dropoffOptionsQuery.data],
  );
  const dropoffEnabledForTeam =
    dropoffOptionsQuery.data?.dropoffEnabledForTeam ?? false;

  const selectedShipping = createLabelInput.inputs.selectedShipping.value;
  const selectedDropoff =
    selectedShipping?.method === "dropoff"
      ? selectedShipping.dropoff
      : undefined;

  const labelRateInUse = useMemo(() => {
    const selected =
      selectedShipping?.method === "label" ? selectedShipping.label : undefined;
    if (!rates.length) {
      return undefined;
    }
    if (selected && rates.some((r) => r.id === selected.id)) {
      return selected;
    }
    return rates[0];
  }, [rates, selectedShipping]);

  const originalLabelCost = labelRatesQuery.data?.originalLabelCost ?? 0;
  const shopperFee = return_.totals?.fee ?? 0;
  const redoMaxLabelPrice =
    team?.settings?.returns?.redoMaxLabelPrice ??
    MAX_LABEL_PRICE_DEFAULT.amount;

  // Merchant cost of the drop-off being edited (its provider's current cost) —
  // the difference baseline for a re-picked drop-off or a label superseding it.
  const previousDropoffMerchantCost = useMemo(() => {
    if (!editingDropoffGroupId) {
      return undefined;
    }
    const prevProvider = return_.dropoffs?.qrCodes?.find(
      (q) => q.shipmentGroupId === editingDropoffGroupId,
    )?.provider;
    if (!prevProvider) {
      return 0;
    }
    return dropoffOptions.find((o) => o.provider === prevProvider)?.fee ?? 0;
  }, [editingDropoffGroupId, return_.dropoffs?.qrCodes, dropoffOptions]);

  // A drop-off replacing a label needs the carrier rates to credit the replaced
  // label; a plain drop-off does not.
  const isReplacingLabelWithDropoff =
    !editingDropoffGroupId &&
    labelType === PurchaseLabelType.Replace &&
    shipmentId != null;
  const dropoffPriceLoading =
    isReplacingLabelWithDropoff && labelRatesQuery.isFetching;

  const previousDropoffResponsibility = useMemo(() => {
    if (!editingDropoffGroupId || !previousDropoffMerchantCost) {
      return 0;
    }
    return getReturnShippingMerchantCost({
      return_,
      newAmount: previousDropoffMerchantCost,
      labelType: PurchaseLabelType.New,
      originalLabelCost: shopperFee,
      redoMaxLabelPrice,
      replacedGroupId: editingDropoffGroupId,
    });
  }, [
    return_,
    editingDropoffGroupId,
    previousDropoffMerchantCost,
    shopperFee,
    redoMaxLabelPrice,
  ]);

  const merchantCost = useMemo(() => {
    if (selectedShipping?.method === "dropoff") {
      const fee = selectedDropoff?.fee;
      if (fee == null || dropoffPriceLoading) {
        return 0;
      }
      return getReturnShippingMerchantCost({
        return_,
        newAmount: fee,
        labelType: PurchaseLabelType.New,
        originalLabelCost: shopperFee,
        redoMaxLabelPrice,
        // Replacing a label credits its raw billed cost; editing a drop-off
        // credits the prior drop-off's coverage-adjusted responsibility.
        replacedMerchantCost: isReplacingLabelWithDropoff
          ? originalLabelCost
          : previousDropoffResponsibility,
        replacedGroupId: editingDropoffGroupId,
      });
    }
    if (!labelRateInUse) {
      return 0;
    }
    return getReturnShippingMerchantCost({
      return_,
      newAmount: parseFloat(labelRateInUse.rate),
      labelType,
      originalLabelCost,
      redoMaxLabelPrice,
      replacedMerchantCost: editingDropoffGroupId
        ? previousDropoffResponsibility
        : 0,
      replacedGroupId: editingDropoffGroupId,
      isMerchantCarrierAccount:
        labelRateInUse.is_merchant_carrier_account ?? false,
    });
  }, [
    selectedShipping,
    selectedDropoff,
    labelRateInUse,
    labelType,
    originalLabelCost,
    shopperFee,
    redoMaxLabelPrice,
    isReplacingLabelWithDropoff,
    previousDropoffResponsibility,
    editingDropoffGroupId,
    dropoffPriceLoading,
    return_,
  ]);

  const isFirstLabel = getIsFirstLabel(return_, editingDropoffGroupId);
  const isFirstLabelOrReplacingFirstLabel =
    isFirstLabel ||
    (labelType === PurchaseLabelType.Replace &&
      (return_.shipments?.length ?? 0) === 1);

  // Editing a drop-off shows option rows as a replacement against the prior
  // drop-off cost, without overloading the real target labelType elsewhere.
  const optionLabelType = editingDropoffGroupId
    ? PurchaseLabelType.Replace
    : labelType;
  const optionOriginalLabelCost = editingDropoffGroupId
    ? (previousDropoffMerchantCost ?? 0)
    : originalLabelCost;

  return {
    fromAddress,
    toAddress,
    labelRatesQuery,
    rates,
    labelRateInUse,
    originalLabelCost,
    merchantCost,
    dropoffOptions,
    dropoffEnabledForTeam,
    dropoffOptionsLoading: dropoffOptionsQuery.isFetching,
    selectedDropoff,
    dropoffPriceLoading,
    previousDropoffMerchantCost,
    optionLabelType,
    optionOriginalLabelCost,
    isFirstLabel,
    isFirstLabelOrReplacingFirstLabel,
    redoMaxLabelPrice,
  };
}
